import { Embedded, EmbeddedUser } from './embed.model';

export type embed = keyof EmbedProperties;

interface EmbedProperties {
    $who: {id?: string; fullName?: string; avatar?: string};
    $previousWho: {id?: string; fullName?: string; avatar?: string};
}

export enum AuditEntity {
    APP = 'app',
    ACL = 'acl',
    CELL = 'cell',
    USER = 'user',
    PROJECT = 'project',
    COMPANY = 'company',
    INVENTORY = 'inventory',
    BUSINESS_PROJECT = 'business-project',
    OPPORTUNITY = 'opportunity',
    GDPR = 'gdpr',
    ASSIGNMENT_GROUP_CONFIG = 'assignment-group-config',
    TOOL_AND_SERVICE = 'tool-and-service',
}

export const auditTitleByEntity = {
    [AuditEntity.APP]: 'App',
    [AuditEntity.CELL]: 'Cellule',
    [AuditEntity.USER]: 'Utilisateur',
    [AuditEntity.PROJECT]: 'Projet',
    [AuditEntity.COMPANY]: 'Compagnie',
    [AuditEntity.INVENTORY]: 'Inventaire',
    [AuditEntity.BUSINESS_PROJECT]: 'Business projet',
    [AuditEntity.OPPORTUNITY]: 'Opportunité',
    [AuditEntity.GDPR]: 'RGPD',
    [AuditEntity.ASSIGNMENT_GROUP_CONFIG]: 'Matrice d\'escalade',
    [AuditEntity.ACL]: 'Autorisation',
    [AuditEntity.TOOL_AND_SERVICE]: 'Outils & Services',
};

export const hiddenResourceKeysByEntity: Record<string, string[]> = {
    [AuditEntity.TOOL_AND_SERVICE]: ['inventoryId'],
};

export type EmbedLog<T extends embed> = Log & Pick<EmbedProperties, T>;
export type EmbedLogWithContent<T extends embed> = LogWithContent & Pick<EmbedProperties, T>;

export const configuration: {[K in embed]: {embed: string; transform: (log: Log, embedded: Embedded) => EmbedProperties[K] }} = {
    $who: {
        embed: 'users',
        transform: ({who}: Log, {users: embeddedUsers}: Embedded): EmbeddedUser => {
            const user = embeddedUsers[who];
            return new EmbeddedUser(user);
        }
    },
    $previousWho: {
        embed: 'users',
        transform: ({who}: Log, {users: embeddedUsers}: Embedded): EmbeddedUser => {
            const user = embeddedUsers[who];
            return new EmbeddedUser(user);
        }
    },
};

export class Log {
    public id: string;
    public resourceId: string;
    public entity: string;
    public updatedFields: string[];
    public when: string;
    public who: string;
    public previousWhen: string;
    public previousWho: string;
    public resource: string;

    constructor(data: any = {}) {
        this.id = data.id;
        this.resourceId = data.resourceId;
        this.entity = data.entity;
        this.updatedFields = data.updatedFields;
        this.when = data.when;
        this.who = data.who;
        this.previousWhen = data.previousWhen;
        this.previousWho = data.previousWho;
        this.resource = data.resource;
    }

    public static toLogsEmbedded<T extends embed>(data: Log[], embedded: Embedded, embedFields: T[]): EmbedLog<T>[] {
        return data.map(log => Log.toLogEmbedded(log, embedded, embedFields));
    }

    public static toLogEmbedded<T extends embed>(data: Log, embedded: Embedded, embedFields: T[]): EmbedLog<T> {
        const customData = embedFields.reduce((acc, field: embed) => {
            acc[field] = configuration[field].transform(data, embedded);
            return acc;
        }, {});
        return {...data, ...customData} as EmbedLog<T>;
    }
}

export class LogWithContent extends Log {
    public content: LogContent[];

    constructor(data: any = {}) {
        super(data);
        this.content = (data.content || []).map(content => new LogContent(content));
    }

    public static toLogWithContentEmbedded<T extends embed>(data: Log, embedded: Embedded, embedFields: T[]): EmbedLogWithContent<T> {
        const customData = embedFields.reduce((acc, field: embed) => {
            acc[field] = configuration[field].transform(data, embedded);
            return acc;
        }, {});
        return {...data, ...customData} as EmbedLogWithContent<T>;
    }
}

export class LogContent {
    public op: string;
    public path: string;
    public current: string;
    public previous: string;
    public added: string[];
    public removed: string[];

    constructor(data: any = {}) {
        this.op = data.op;
        this.path = data.path;
        this.current = data.current;
        this.previous = data.previous;
        this.added = data.added;
        this.removed = data.removed;
    }
}
